/* eslint-disable max-lines */
import React, {
  forwardRef,
  useEffect,
  useMemo,
  useState,
  useCallback
} from 'react';
import { ActionMeta } from 'react-select';
import cn from 'classnames';
import { LabelValueSelectInput } from '../../../utils/types/shared/label-value-select-input';
import { useThemeContext } from '../../../utils/providers/theme-context';
import {
  ComboboxMulti,
  ComboboxMultiLabel,
  ComboboxMultiOption
} from './combobox-multi';
import { Combobox, ComboboxLabel, ComboboxOption } from './combobox';

type ColorToken =
  | 'activeText'
  | 'normalText'
  | 'secondLayer'
  | 'border'
  | 'borderPrimary'
  | 'inputBorder'
  | 'icon'
  | 'inactiveText'
  | 'dropdownText'
  | 'baseLayer'
  | 'firstLayer'
  | 'thirdLayer'
  | 'datagridBg';

type OptionType = { label: string; value: string | number };
type CSSWithVars = React.CSSProperties & {
  [key: `--${string}`]: string | number | undefined;
};

interface CustomSelectInputProps {
  id?: string;
  name?: string;
  options: LabelValueSelectInput[] | OptionType[];
  value?:
    | LabelValueSelectInput
    | OptionType
    | { label: string; value: string | undefined }
    | null
    | string
    | number
    | undefined
    | (LabelValueSelectInput | OptionType | string | number)[];
  defaultValue?: LabelValueSelectInput | null;
  /* eslint-disable @typescript-eslint/no-explicit-any */
  onChange: (
    selectedOption: any,
    actionMeta?: ActionMeta<LabelValueSelectInput>
  ) => void;
  /* eslint-enable @typescript-eslint/no-explicit-any */
  onInputChange?: (inputValue: string) => void;
  placeholder?: string;
  menuPlacement?: 'auto' | 'top' | 'bottom';
  size?: 'small' | 'medium' | 'large';
  className?: string;
  wrapperClass?: string;
  labelClass?: string;
  label?: string | React.ReactNode;
  error?: string;
  helperText?: string | React.ReactNode;
  isClearable?: boolean;
  isSearchable?: boolean;
  isMulti?: boolean;
  menuPortalTarget?: HTMLElement | null;
  menuPosition?: 'fixed' | 'absolute';
  useCustomDropdownIndicator?: boolean;
  styles?: Record<string, (base: React.CSSProperties) => React.CSSProperties>;
  renderOption?: (
    option: LabelValueSelectInput | OptionType
  ) => React.ReactNode;
  compactOptions?: boolean;
}

const SelectInput = forwardRef<HTMLDivElement, CustomSelectInputProps>(
  (props, ref) => {
    const {
      id,
      name,
      options = [],
      value,
      onChange,
      onInputChange,
      placeholder = 'Select...',
      menuPlacement = 'bottom',
      size = 'medium',
      className,
      wrapperClass,
      labelClass,
      label,
      error,
      renderOption,
      compactOptions,
      helperText
    } = props;

    const themeContext = useThemeContext();
    const theme = themeContext?.theme ?? 'light';
    const brandColor = themeContext?.brandColor ?? null;
    const getColour = themeContext?.getColour;

    const pick = (light: string, dark: string) =>
      theme === 'dark' ? dark : light;

    const safeColour = (token: ColorToken): string =>
      getColour?.(token) ?? 'inherit';

    const getBrandTint = themeContext?.getBrandTint;
    const active = brandColor ?? safeColour('activeText');
    const text = safeColour('normalText');
    const surface = safeColour('secondLayer');
    const borderBase = safeColour('border');
    const borderHover = safeColour('border');
    const placeholderColor = safeColour('inactiveText');
    const disabledBg = pick('rgba(17,24,39,0.03)', 'rgba(255,255,255,0.06)');

    const controlBg = theme === 'dark' ? 'transparent' : surface;
    const menuBg = surface;
    const ring = active;

    const selectShadow =
      '0 1px 3px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.04)';
    const selectFocusShadow =
      '0 2px 6px rgba(0,0,0,0.10), 0 1px 3px rgba(0,0,0,0.06)';

    const styleVars: CSSWithVars = {
      '--in-bg': surface,
      '--in-fg': text,
      '--in-ph': placeholderColor,
      '--in-border': borderBase,
      '--in-border-hover': borderHover,
      '--in-ring': ring,
      '--in-disabled-bg': disabledBg,
      '--in-ctrl-bg': controlBg,
      '--in-menu-bg': menuBg,
      '--in-shadow': selectShadow,
      '--in-focus-shadow': selectFocusShadow
    };

    const labelClasses = cn(
      'block text-sm font-semibold mb-2 pl-1',
      labelClass
    );

    const normalizeOptions = (
      opts: (LabelValueSelectInput | OptionType)[]
    ): LabelValueSelectInput[] =>
      opts.map(opt => ({
        ...opt,
        value: String(opt.value),
        label: opt.label
      }));

    const safeOptions = useMemo(
      () => (Array.isArray(options) ? normalizeOptions(options) : []),
      [options]
    );

    const getCurrentValue = (): LabelValueSelectInput | OptionType | null => {
      if (!value) return null;
      if (Array.isArray(value)) return null;

      if (typeof value === 'object' && value !== null && 'value' in value) {
        if ((value as { value: unknown }).value === undefined) return null;
        return value as LabelValueSelectInput | OptionType;
      }

      const matchedOption = options.find(
        option => option.value === value || option.value === String(value)
      );

      return matchedOption || null;
    };

    const getCurrentMultiValue = (): LabelValueSelectInput[] => {
      if (!value) return [];
      if (Array.isArray(value)) {
        return value
          .map(val => {
            if (typeof val === 'object' && val !== null && 'value' in val) {
              const typed = val as LabelValueSelectInput | OptionType;
              return {
                ...typed,
                value: String(typed.value),
                label: typed.label
              } as LabelValueSelectInput;
            }

            const matchedOption = options.find(
              option => option.value === val || option.value === String(val)
            );

            return matchedOption
              ? ({
                  ...matchedOption,
                  value: String(matchedOption.value),
                  label: matchedOption.label
                } as LabelValueSelectInput)
              : null;
          })
          .filter(Boolean) as LabelValueSelectInput[];
      }
      return [];
    };

    const currentValue = getCurrentValue();
    const currentMultiValue = getCurrentMultiValue();

    // ✅ IMPORTANT: keep HeadlessUI happy (value type is T | undefined), not null
    const [internalValue, setInternalValue] = useState<
      LabelValueSelectInput | undefined
    >(
      currentValue
        ? {
            ...currentValue,
            value: String(currentValue.value),
            label: currentValue.label
          }
        : undefined
    );

    useEffect(() => {
      const newValue = currentValue
        ? {
            ...currentValue,
            value: String(currentValue.value),
            label: currentValue.label
          }
        : undefined;

      setInternalValue(newValue);
    }, [currentValue]);

    const [serverInput, setServerInput] = useState<string>('');
    const isServerSearch = typeof onInputChange === 'function';

    const emitInputChange = useCallback(
      (val: string) => {
        setServerInput(val);
        onInputChange?.(val);
      },
      [onInputChange]
    );

    // ✅ MUST return string only (never undefined)
    const displayValue = (option: LabelValueSelectInput | OptionType | null) =>
      option && typeof option.label === 'string' ? option.label : '';

    const handleComboboxChange = (
      selectedOption: LabelValueSelectInput | null
    ) => {
      setInternalValue(selectedOption ?? undefined); // ✅ undefined instead of null

      let resultOption: LabelValueSelectInput | null = selectedOption;
      if (selectedOption && options.length > 0) {
        const originalOption = options.find(
          opt => String(opt.value) === selectedOption.value
        );
        if (originalOption)
          resultOption = originalOption as LabelValueSelectInput;
      }

      onChange(resultOption, {
        action: resultOption ? 'select-option' : 'clear'
      } as ActionMeta<LabelValueSelectInput>);
    };

    const controlClasses = cn(
      'w-full rounded-2xl border transition-all',
      'bg-[var(--in-ctrl-bg)] text-[var(--in-fg)]',
      'border-[var(--in-border)] hover:border-[var(--in-border-hover)]',
      'focus:outline-none focus:ring-1 focus:ring-[var(--in-ring)] focus:border-[var(--in-ring)]',
      'placeholder-[var(--in-ph)]',
      'shadow-[var(--in-shadow)] focus:shadow-[var(--in-focus-shadow)]',
      {
        'h-9 text-sm px-2': size === 'small',
        'h-10 text-sm px-3': size === 'medium',
        'h-12 text-base px-3': size === 'large'
      },
      className
    );

    const optionClasses = cn(
      'data-[active=true]:bg-[var(--in-border-hover)] data-[selected=true]:bg-[var(--in-ring)] data-[selected=true]:text-white text-[var(--in-fg)]',
      compactOptions && '!py-1 !sm:py-0.5'
    );

    const selectedSet = useMemo(() => {
      const mv = getCurrentMultiValue();
      return new Set(mv.map(o => String(o.value)));
    }, [value, options]);

    const valueForCombobox = useMemo(
      () => safeOptions.filter(o => selectedSet.has(String(o.value))),
      [safeOptions, selectedSet]
    );

    return (
      <div
        ref={ref}
        className={wrapperClass ?? 'w-full'}
        style={styleVars}
        data-scope="select-input"
      >
        {label && (
          <label htmlFor={id} className={labelClasses}>
            {label}
          </label>
        )}

        <div className="relative">
          {name && (
            <input
              type="hidden"
              name={name}
              value={
                props.isMulti && currentMultiValue
                  ? currentMultiValue.map(v => String(v.value)).join(',')
                  : currentValue
                    ? String(currentValue.value)
                    : ''
              }
            />
          )}

          {props.isMulti ? (
            <ComboboxMulti
              by="value"
              value={valueForCombobox}
              onChange={newSelected => {
                const resultOptions = newSelected.map(sel => {
                  const original = options.find(
                    opt => String(opt.value) === String(sel.value)
                  );
                  return (original as LabelValueSelectInput) || sel;
                });
                onChange(resultOptions, {
                  action: 'select-option'
                } as ActionMeta<LabelValueSelectInput>);
              }}
              options={safeOptions}
              displayValue={(option: LabelValueSelectInput | null) =>
                option
                  ? typeof option.label === 'string'
                    ? option.label
                    : ''
                  : ''
              }
              placeholder={placeholder}
              anchor={menuPlacement === 'top' ? 'top' : 'bottom'}
              aria-label={typeof label === 'string' ? label : undefined}
              className={controlClasses}
              serverFilter={isServerSearch}
              onQueryChange={isServerSearch ? emitInputChange : undefined}
              onInputChange={isServerSearch ? emitInputChange : undefined}
              inputValue={isServerSearch ? serverInput : undefined}
              renderChip={(option, onRemove) => (
                <span
                  className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium"
                  style={{
                    backgroundColor:
                      getBrandTint?.(0.12) ?? 'rgba(37,99,235,0.12)',
                    color: active
                  }}
                >
                  {typeof option.label === 'string' ? option.label : ''}
                  <button
                    type="button"
                    onClick={onRemove}
                    className="inline-flex items-center justify-center rounded-full opacity-60 hover:opacity-100 transition-opacity"
                  >
                    <svg
                      className="h-3 w-3"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </span>
              )}
            >
              {option => (
                <ComboboxMultiOption
                  key={option.value}
                  value={option}
                  className={optionClasses}
                >
                  <ComboboxMultiLabel>
                    {renderOption ? renderOption(option) : option.label}
                  </ComboboxMultiLabel>
                </ComboboxMultiOption>
              )}
            </ComboboxMulti>
          ) : (
            <Combobox
              value={internalValue} // ✅ T | undefined
              onChange={handleComboboxChange}
              options={safeOptions}
              displayValue={displayValue} // ✅ string only
              placeholder={placeholder}
              anchor={menuPlacement === 'top' ? 'top' : 'bottom'}
              aria-label={typeof label === 'string' ? label : undefined}
              className={controlClasses}
              serverFilter={isServerSearch}
              onQueryChange={isServerSearch ? emitInputChange : undefined}
            >
              {option => (
                <ComboboxOption
                  key={option.value}
                  value={option}
                  className={optionClasses}
                >
                  <ComboboxLabel>
                    {renderOption ? renderOption(option) : option.label}
                  </ComboboxLabel>
                </ComboboxOption>
              )}
            </Combobox>
          )}
        </div>

        <style>{`
        [data-scope="select-input"] .popover-panel,
        [data-scope="select-input"] .combobox-panel,
        [data-scope="select-input"] .menu,
        [data-scope="select-input"] [role="listbox"] {
          background-color: var(--in-menu-bg);
          color: var(--in-fg);
          border: 1px solid var(--in-border);
          overflow: hidden;
        }
      `}</style>

        {helperText && !error && (
          <p className="text-xs mt-1" style={{ color: placeholderColor }}>
            {helperText}
          </p>
        )}
        {error && (
          <p
            className="text-sm mt-1"
            style={{ color: getColour?.('danger') ?? 'red' }}
          >
            {error}
          </p>
        )}
      </div>
    );
  }
);

SelectInput.displayName = 'SelectInput';

export default SelectInput;
